// Connect — the recipient-facing landing page for connecting an AI coding
// tool to BlueRock. Public, unauthenticated (see ui/src/App.tsx's route
// list — this one lives outside RequireAuth), and carries NO host-specific
// data: the Enrollment URL and Dev host an invited user needs are delivered
// separately, as plain text in whatever message their admin sent them
// (ConnectModal's "Copy message" button generates that text) — see
// docs/superpowers/specs/2026-09-10-sso-enrollment-distribution-design.md
// for why. Because this page has no data dependency, ONE link (never
// regenerated, never host-scoped) works for every host, every fleet,
// forever.
//
// Scope is deliberately narrow (2026-09-10, Curt): this page's only job is
// pointing someone at the right installer/extension for their tool. Once
// that's installed and opened, the tool/Connector Desktop's OWN UI takes
// over — prompting for Enrollment URL, Dev host, and SSO sign-in itself.
// This page used to also narrate those in-app steps; that content didn't
// belong here and has been removed rather than kept in sync with a UI this
// page doesn't control.
import { useState } from "react";
import { Button, theme } from "antd";
import { ArrowUpRight, Download } from "lucide-react";

// The extension's real identifier is BlueRock.bluerock-connector — a prior
// version of this file (and the ConnectModal it was copied from) used
// "bluedevx", which 404s on both registries and was never real. Confirmed
// live (2026-09-10) via the Marketplace's own gallery API and the Open VSX
// API: publisher "BlueRock", verified, "Works with Windows x64, macOS
// Apple Silicon, macOS Intel, Linux ARM64, Linux x64" — one extension for
// both VS Code and Cursor, not two different ones.
const VSCODE_EXTENSION_URL = "https://marketplace.visualstudio.com/items?itemName=BlueRock.bluerock-connector";
const CURSOR_EXTENSION_URL = "https://open-vsx.org/extension/BlueRock/bluerock-connector";
// Real BlueRock Connector binaries (confirmed live, 2026-09-10) — same URLs
// the public learn.bluerock.io/get-started guide links under "Download the
// Connector for your computer". That page describes the managed-trial flow;
// the binary itself is the same download either way.
const CONNECTOR_MACOS_URL = "https://bluerock-public.s3.us-east-1.amazonaws.com/tools/macos/BlueRock_Connector_macos";
const CONNECTOR_WINDOWS_URL = "https://bluerock-public.s3.us-east-1.amazonaws.com/tools/windows/BlueRock_Connector_win64";

// The two tool groups share identical setup content within each group (both
// desktop apps go through BlueRock Connector Desktop; both editors go
// through the same extension), so there's no reason to make a visitor pick
// among four options — two is the real number of distinct installers.
// Logo assets are each vendor's own official mark: Claude and Cursor as
// SVGs (simple-icons, CC0 markup, brand colors applied explicitly), VS Code
// and OpenAI as PNGs pulled directly from code.visualstudio.com and
// OpenAI's own GitHub org avatar — see ui/public/logos/.
type ToolGroup = "desktop" | "vscode";

export default function Connect() {
  const [group, setGroup] = useState<ToolGroup>("desktop");
  return (
    <div className="flex h-full justify-center overflow-y-auto bg-surface px-6 py-16">
      <div className="w-full max-w-xl">
        <h1 className="mb-8 text-2xl font-semibold text-text-primary">Connect your AI coding tools to BlueRock</h1>

        <div className="mb-8">
          <div className="mb-3 text-base font-semibold text-text-primary">Which tool are you using?</div>
          <div className="grid grid-cols-2 gap-4">
            <ToolCard
              selected={group === "desktop"}
              onClick={() => setGroup("desktop")}
              label="Claude Code Desktop & Codex"
              logos={
                <>
                  <img src="/logos/claude.svg" alt="Claude" className="h-7 w-7" />
                  <img src="/logos/openai.png" alt="OpenAI Codex" className="h-7 w-7 rounded-full object-contain" />
                </>
              }
            />
            <ToolCard
              selected={group === "vscode"}
              onClick={() => setGroup("vscode")}
              label="VS Code & Cursor"
              logos={
                <>
                  <img src="/logos/vscode.png" alt="VS Code" className="h-7 w-7 object-contain" />
                  <img src="/logos/cursor.svg" alt="Cursor" className="h-7 w-7" />
                </>
              }
            />
          </div>
        </div>

        {group === "vscode" ? (
          <div>
            <div className="text-[13.5px] font-semibold text-text-primary">Add the BlueRock extension</div>
            <p className="mt-1 text-sm text-text-secondary">
              VS Code and Cursor both connect to a BlueRock sandbox through the same BlueRock Connector extension.
              Once installed, Connector walks you through everything else. Choose <strong>Setup</strong> and have
              your Enrollment URL and Dev host handy.
            </p>
            <div className="mt-3 flex gap-2">
              <Button href={VSCODE_EXTENSION_URL} target="_blank" rel="noopener noreferrer">
                Add to VS Code <ArrowUpRight size={12} className="ml-1 inline align-[-1px]" />
              </Button>
              <Button href={CURSOR_EXTENSION_URL} target="_blank" rel="noopener noreferrer">
                Add to Cursor <ArrowUpRight size={12} className="ml-1 inline align-[-1px]" />
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-[13.5px] font-semibold text-text-primary">Install BlueRock Connector Desktop</div>
            <p className="mt-1 text-sm text-text-secondary">
              Claude Code Desktop and Codex both connect to a BlueRock sandbox through BlueRock Connector Desktop for
              Mac and Windows. Once installed, Connector walks you through everything else. Choose <strong>Connect
              with SSO</strong> and have your Enrollment URL and Dev host handy.
            </p>
            <div className="mt-3 flex gap-2">
              <Button href={CONNECTOR_MACOS_URL} target="_blank" rel="noopener noreferrer">
                Download for macOS <Download size={12} className="ml-1 inline align-[-1px]" />
              </Button>
              <Button href={CONNECTOR_WINDOWS_URL} target="_blank" rel="noopener noreferrer">
                Download for Windows <Download size={12} className="ml-1 inline align-[-1px]" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// A full-width card acting as a content tab — selecting one swaps the
// install instructions below.
function ToolCard({ selected, onClick, label, logos }: { selected: boolean; onClick: () => void; label: string; logos: React.ReactNode }) {
  const { token } = theme.useToken();
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border p-4 text-left transition-colors"
      style={{
        borderColor: selected ? token.colorPrimary : token.colorBorder,
        backgroundColor: selected ? token.colorPrimaryBg : token.colorBgContainer,
      }}
    >
      <div className="flex items-center gap-2">{logos}</div>
      <div className="mt-3 text-sm font-semibold text-text-primary">{label}</div>
    </button>
  );
}
